import { translateBackend } from '@assets/i18n/config';
import type { BankAccount } from '@interfaces/bankAccount';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { MenuItem } from '@mui/material';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { Form } from '@ovComponents/2-component/form/form';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { dollars2cents } from '@utils/dollars2cents';
import dayjs from 'dayjs';
import { Box, Button, Grid, Typography, useTheme } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

enum Frequencies {
  ONE_TIME = 'ONE_TIME',
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  SEMI_MONTHLY = 'SEMI_MONTHLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

export type BankAccountTransfer = {
  amountCents: number;
  bankAccount: {
    id: string;
    name: string;
    bankAccountNumber: string;
    bankAccountNumberDisclosed?: string;
    institutionNumber?: string;
    transitNumber?: string;
    transitNumberDisclosed?: string;
    accountNumber?: string;
  };
  frequency?: Frequencies;
  scheduledDate: string;
  subAccount?: SubAccount;
  isRecurring?: boolean;
};

type BankAccountTransferDialogContentProps = {
  onTransferCreated: (transfer: BankAccountTransfer) => void;
  userData: any;
  onDialogClose: () => void;
  disclaimer?: TranslatedString | null;
};

const BankAccountTransferDialogContent = ({
  onTransferCreated,
  userData,
  onDialogClose,
  disclaimer,
}: BankAccountTransferDialogContentProps) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'affiliationTypes',
    'components',
  ]);
  const [inboundAccount, setInboundAccount] = useState<
    SubAccount | undefined
  >();
  const [frequency, setFrequency] = useState<Frequencies>();
  const [bank, setBank] = useState<BankAccount | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [amount, setAmount] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const theme = useTheme();
  const submit = () => {
    if (!bank) return;
    onTransferCreated({
      amountCents: dollars2cents(amount),
      bankAccount: {
        ...bank,
      },
      frequency,
      scheduledDate,
      subAccount: inboundAccount,
      isRecurring,
    });
  };

  useEffect(() => {
    if (!isRecurring) {
      setFrequency(undefined);
      setScheduledDate('');
    }
  }, [isRecurring]);

  const recurrenceFieldsValid =
    !isRecurring || (scheduledDate !== '' && !!frequency);
  const allFieldsValid =
    bank !== null && !!inboundAccount && amount !== '' && recurrenceFieldsValid;

  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:bankAccountTransferCreation.title')}
      </Typography>
      <SubAccountSelect
        sx={{ mt: '24px' }}
        selectedSubAccount={inboundAccount}
        omitAny
        onSubAccountSelect={setInboundAccount}
        userId={userData.id}
        label={t('components:transferModal.portfolio')}
      />
      <BankAccountSelectField
        userId={userData.id}
        bankAccount={bank}
        setBankAccount={setBank}
        discloseBankAccountNumber
        sx={{ mt: 3 }}
      />
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'end',
          mt: '20px',
          mb: '24px',
        }}
      >
        <Grid size={4}>
          <AmountField amount={amount} setAmount={setAmount} />
        </Grid>
        {isRecurring ? (
          <Grid size={4}>
            <SelectField
              onChange={(e: any) => setFrequency(e.target.value)}
              label={t('components:transferModal.schedule')}
              fullWidth
              value={frequency}
            >
              {Object.values(Frequencies).map((freq) => (
                <MenuItem value={freq}>
                  {t(`components:transferModal.scheduleOptions.${freq}`)}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
        ) : undefined}
        {isRecurring ? (
          <Grid size={4}>
            <DateField
              label={t('components:transferModal.starting')}
              value={scheduledDate}
              onChange={(date: any) =>
                setScheduledDate(dayjs(date?.toString()).format('YYYY-MM-DD'))
              }
            />
          </Grid>
        ) : undefined}
      </Grid>
      <CheckboxField
        label={t(
          'workflowCompletions:bankAccountTransferCreation.makeRecurring',
        )}
        checked={isRecurring}
        onChange={(value) => setIsRecurring(value)}
      />
      <br></br>
      {disclaimer && (
        <Box
          sx={{
            mb: 1,
            background: theme.color.surfaceContainerVariant,
            borderRadius: theme.borderRadius.lg,
            p: 2,
          }}
        >
          <Typography variant='bodySmall' component='div'>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(disclaimer)}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button
          color='primary'
          disabled={!allFieldsValid}
          sx={{ mt: 3 }}
          type='submit'
        >
          {t('workflowCompletions:addTransfer')}
        </Button>
      </Box>
    </Form>
  );
};

export default BankAccountTransferDialogContent;
